import { useEffect, useRef } from 'react';

import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import { correction, positionNow } from './sync';

/** How often drift is measured. Four times a second is well below audible. */
const TICK_MS = 250;

/**
 * Keeps the one `<audio>` element in step with a room (§6.8).
 *
 * ## Why this lives beside the element rather than in the room page
 *
 * The correction has to touch `currentTime` and `playbackRate`, and the element
 * is owned by `PlayerHost` and never unmounts. Doing it from the room page
 * would mean the audio drifted free the moment somebody navigated away while
 * still listening - which is exactly the case §9.1 exists to protect.
 *
 * ## What it does, four times a second
 *
 * Works out where the room *should* be from the last known position and the
 * clock offset, compares it to where the audio actually is, and applies the
 * smallest correction that closes the gap:
 *
 * - under 120ms: nothing, and the rate goes back to 1
 * - under 1.5s: bend the playback rate by up to 4%, which nobody can hear
 * - beyond that: seek, because rate alone would take a minute to catch up
 *
 * The middle case is the whole point. Two browsers decoding the same stream are
 * never sample-accurate, and correcting every small difference with a seek is
 * audible as a stutter - far worse than being 200ms behind.
 */
export function useRoomSync(audioRef: React.RefObject<HTMLAudioElement | null>): void {
  const tuned = useRoomTuning((state) => state.tuned);
  const rateRef = useRef(1);
  /**
   * Whether the room was paused last time this looked.
   *
   * Only used to spot the moment it stops being paused - see the note in the
   * tick. A boolean rather than a count because all that matters is the edge.
   */
  const wasPausedRef = useRef(false);
  /**
   * The room being followed last time this looked.
   *
   * Used to spot the moment it becomes a *different* one - including the moment
   * it becomes one at all, from nothing. See `justTuned` in the tick.
   */
  const followingRef = useRef<string | null>(null);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || tuned === null) {
      /*
       * Untuned playback runs at normal speed.
       *
       * Without this, leaving a room mid-correction leaves the element at
       * 1.04x for ever, and every track afterwards is very slightly sharp -
       * the sort of bug somebody reports as "the site sounds wrong".
       */
      if (audio && rateRef.current !== 1) {
        audio.playbackRate = 1;
        rateRef.current = 1;
      }

      /*
       * Forgotten, so tuning back into the same room counts as new.
       *
       * Without this the edge below only fires when the room *changes*, and a
       * host ending a broadcast and starting another is the same room every
       * time - which is exactly the case that was silent.
       */
      followingRef.current = null;

      return;
    }

    /*
     * Tuning into a room is a reason to start playing.
     *
     * ## The bug this fixes
     *
     * A host went on air, ended the broadcast, then went on air again - and the
     * second time nothing played. `RoomFollower` stops listening when a room
     * reports offline, and stopping pauses the element, which fires `pause` and
     * clears `playing` in the player store. Going live again re-tunes, and the
     * resume below then asked whether `player.playing` was true. It was false,
     * because ending had just made it false. And `wasPausedRef` was false too -
     * the room had gone *offline*, which is not the same as paused, so that
     * edge never fired either.
     *
     * The result was a host looking at their own broadcast, on air, in silence,
     * with no control that would start it. It happened on every second
     * broadcast of a session and had nothing to do with which mode they picked.
     *
     * Newly following a room is exactly the case where overriding a stale local
     * pause is right: nobody tunes into a broadcast in order not to hear it.
     */
    const justTuned = followingRef.current !== tuned.username;

    followingRef.current = tuned.username;

    if (justTuned) wasPausedRef.current = true;

    const tick = () => {
      const { playhead } = useRoomTuning.getState();
      const player = usePlayer.getState();

      if (!playhead || playhead.position_ms === null) return;

      /*
       * A track change is not a drift.
       *
       * The room has moved on and this element is still on the old track;
       * seeking would put the wrong audio at the right timestamp. The component
       * watching the room loads the new one - all this can do is not make it
       * worse.
       */
      if (playhead.track_id !== null && player.track?.id !== playhead.track_id) return;

      // A paused room stays where it is, and so does the listener.
      if (playhead.paused) {
        if (!audio.paused) audio.pause();

        wasPausedRef.current = true;

        return;
      }

      /*
       * The room starting again has to override a local pause, and only then.
       *
       * `audio.pause()` above fires the element's `pause` event, which sets
       * `playing: false` in the player store - so a resume guarded on
       * `player.playing` could never fire, because pausing had just cleared the
       * thing it tests. A host who paused and pressed play again left every
       * listener silent for the rest of the track, and had to reload to get out
       * of it.
       *
       * The guard is kept for every other case: somebody who paused the dock
       * themselves is not fought four times a second. It is only the *edge* -
       * the room going from paused to playing - that speaks over them, and that
       * is a broadcast resuming, which is not a thing a listener can decline
       * and stay in the room for.
       */
      const roomJustResumed = wasPausedRef.current;

      wasPausedRef.current = false;

      if (audio.paused && (player.playing || roomJustResumed)) {
        void audio.play().catch(() => {});
      }

      const target = positionNow(playhead);

      if (target === null) return;

      // Nothing to correct against until the element has decoded something.
      if (audio.readyState < 1) return;

      const next = correction(audio.currentTime * 1000, target);

      if (next.kind === 'seek') {
        audio.currentTime = next.to / 1000;
        audio.playbackRate = 1;
        rateRef.current = 1;

        return;
      }

      if (next.kind === 'rate' && Math.abs(next.rate - rateRef.current) > 0.001) {
        audio.playbackRate = next.rate;
        rateRef.current = next.rate;
      }
    };

    tick();
    const timer = setInterval(tick, TICK_MS);

    return () => {
      clearInterval(timer);

      if (audioRef.current) {
        audioRef.current.playbackRate = 1;
        rateRef.current = 1;
      }
    };
  }, [tuned, audioRef]);
}
